import { Component, createRef } from 'react'
import { lengthByLocaleFormatter, weightByLocaleFormatter } from '~/utils/formatters'
import Search from '~/components/Search/Search'
import Icon from '~/utils/Icon'
import Input from '~/components/OrdersPage/Input'
import DealerSelect from '~/components/OrdersPage/DealerSelect'
import OrderItemsTable from './OrderItemsTable'
import styles from '~/styles/ordersPage.orderItems.module.css'
import types from '~/types/ordersPage.ts'

export default class OrderItems extends Component {
	static getInitialProps() {
		return {
			namespacesRequired: [ 'Orders', 'common' ],
		}
	}

	constructor(props) {
		super(props)
		this.state = {
			isEditing: false,
			isHiding: false,
		}
		this.date = Date.now()
		if (Object.keys(this.props.order?.orderItems || {}).length && !this.state.orderItems) {
			this.state.orderItems = this._cloneTheObject(this.props.order.orderItems)
			Object.keys(this.state.orderItems).forEach(lineNumber => {
				const line = this.state.orderItems[lineNumber]
				const data = { weight: ((+(line['Item Weight']) || 1) / 1000), locale: 'en', separate: true }
				const { lb, oz } = weightByLocaleFormatter(data)
				this.state[lineNumber + '_lbValue'] = lb
				this.state[lineNumber + '_ozValue'] = oz
			})
		}
		this._onResizeST = null
		this._mainRef = createRef()
		if (this.props.setOnExternalEventCallback) {
			this.props.setOnExternalEventCallback('OrderItems' + this.props.orderId, this._onExternalEvent.bind(this))
		}
		this._onResizeBound = this._onResize.bind(this)
		this._maxTableWidth = 1800 + 'px'
	}

	render(): JSX.Element | null {
		const hasOrderItems = !!(Object.keys(this.props.order?.orderItems || {}).length)
		const isNewOrder = !!(this.props.isNewOrder)
		if (!this.state.isHiding && (hasOrderItems || isNewOrder)) {
			let isEditing = false
			if (this.props.isAbleForEditing) {
				isEditing = this.props.isEditing || this.state.isEditing
			}
			
			let style = {}
			let styleW = {}
			if (this.state.mainWidth) {
				if (!isEditing) {
					styleW.width = this.state.mainWidth
				}
			}
			if (this.state.mainHeight) {
				style.height = this.state.mainHeight
			}
			
			let buttonsClassName = styles.buttons
			if (this.props.hideCaption) {
				buttonsClassName += ' ' + styles.hidden
			}
			
			let partiallyButtonClassName = styles.iconButton + ' ' + styles.splitButton
			let isPartialShipmentMode = (this.props.order.status === 'HOLD' || this.props.order.isPartial)
			if (!(!isEditing && isPartialShipmentMode) || isEditing) {
				partiallyButtonClassName += ' ' + styles.hidden
			} else if (!this.props.currentDealer?.isAbleToSplitOrder) {
				partiallyButtonClassName += ' ' + styles.hidden
			}
			let partiallySignClassName = styles.partiallySign
			if (!(isEditing && isPartialShipmentMode)) {
				partiallySignClassName += ' ' + styles.hidden
			}
			
			let cloneButtonClassName = styles.iconButton + ' ' + styles.cloneButton
			if (!this.props.showCloneButton) {
				if (this.props.hideCloneButton) {
					cloneButtonClassName += ' ' + styles.hidden
				} else {
					if (isEditing) {
						cloneButtonClassName += ' ' + styles.hidden
					}
				}
			}	
			
			
			let editButtonClassName = styles.hidden
			if (!isEditing) {
				if ([ 'CREATED', 'HOLD', 'REJECTED', 'CANCELLED' ].includes(this.props?.order?.status)) {
					editButtonClassName = styles.iconButton
				}
			}
			
			let okButtonClassName = styles.button
			if (this.props.hideOkButton) {
				okButtonClassName += ' ' + styles.hidden
			} else {
				if (this.props.isAbleForEditing) {
					if (!this.props.showOkButton && !isEditing) {
						okButtonClassName += ' ' + styles.hidden
					}
				} else {
					okButtonClassName += ' ' + styles.hidden
				}
			}
			
			let closeButtonClassName = styles.iconButton + ' ' + styles.hide
			if (this.props.hideCloseButton) {
				closeButtonClassName += ' ' + styles.hidden
			}
			
			return (
				<div
					className={styles.main + (this.props.clickedOrder === this.props.order?.id ? '' : ' ' + styles.notCurrent)}
					style={style}
					ref={this._mainRef}
				>
					<div
						className={this.props.order?.multiPackages?.packages_items ? styles.hasPackages : null}
						style={styleW}
					>
						<OrderItemsTable
							order={this.props.order}
							isEditing={isEditing}
							mainWidth={this.state.mainWidth}
							formWidth={this.state.formWidth}
							minTableWidth={this.state.minTableWidth}
							authDealerId={this.props.authDealerId}
							isNewOrder={this.props.isNewOrder}
							orderItems={this.state.orderItems}
							cloneTheObject={this._cloneTheObject}
							onNewOrder={this.props.onNewOrder}
							setIsNewOrder={this.props.setIsNewOrder}
							allDealerOrdersItems={this.props.allDealerOrdersItems}
							isAbleForEditing={this.props.isAbleForEditing}
							toHideTheOrder={this._toHideTheOrder.bind(this)}
							mainRef={this._mainRef}
							updateState={this._updateState.bind(this)}
							newItem={this.state.newItem}
							orderItems={this.state.orderItems}
							searchValue={this.state.searchValue}
							t={this.props.t}
						/>
						<div className={buttonsClassName}>
							<div className={styles.caption}>
								<div className={styles.itemsTitle}>
									<span>
										{this.props.t('Orders:Order Items')}
									</span>
									<div
										className={partiallySignClassName}
									>
										{' (' + this.props.t('Orders:partial shipment mode') + ')'}
									</div>
								</div>
								<div className={styles.rightButtons}>
									<div
										className={closeButtonClassName}
										onClick={this._toHideTheOrder.bind(this)}
									>
										<Icon
											iconName="close"
											iconHint={this.props.t('Close Items Button')}
										/>
									</div>
									<div
										className={editButtonClassName}
										onClick={this._onEditButton.bind(this)}
									>
										<Icon
											iconName="edit"
											iconHint={this.props.t('Edit Button')}
										/>
									</div>
									<div
										className={cloneButtonClassName}
										onClick={this._doClone.bind(this, false)}
									>
										<Icon
											iconName="copy"
											iconHint={this.props.t('Clone Button')}
										/>
									</div>
									<div
										className={partiallyButtonClassName}
										onClick={this._doClone.bind(this, true)}
									>
										<Icon
											iconName="split"
											iconHint={this.props.t('Partially Button')}
										/>
									</div>
									<div
										className={okButtonClassName}
										onClick={this._stopEditing.bind(this)}
										title="OK"
									/>
								</div>
							</div>
						</div>
						<div
							className={styles.saveButtons + (0 ? '' : ' ' + styles.hidden)}
						>
							<div
								className={styles.button}
								onClick={this._onCancelButton.bind(this)}
								title={this.props.t('Orders:Cancel Button')}
							/>
							<div
								className={styles.button}
								onClick={this._onSaveButton.bind(this)}
								title={this.props.t('Orders:Save Button')}
							/>
						</div>
					</div>
				</div>
			)
		} else {
			return null
		}
	}
	
	componentDidUpdate(prevProps, prevState) {
		if (Object.keys(this.props.order?.orderItems || {}).length) {
			if (!this.state.orderItems) {
				this.state.orderItems = this._cloneTheObject(this.props.order.orderItems)
			}
			if (prevProps.isNewOrder && prevProps.isNewOrder !== this.props.isNewOrder) {
				this.setState({ isNotNewOrder: true })
			}
		}
		if (prevProps.isOrderDetailsShown !== this.props.isOrderDetailsShown) {
			if (!this.state.mainWidth) {
				const { mainWidth, minTableWidth } = this._getWidths()
				if (mainWidth) {
					this.setState({ mainWidth })
				}
			}
		}
		this._onResize()
	}
	
	componentDidMount() {
		window?.addEventListener('resize', this._onResizeBound)
	}
	
	componentWillUnmount() {
		this.setState({ isHiding: true }) 
		window?.removeEventListener('resize', this._onResizeBound)
	}

	_updateState(data) {
		this.setState(data)
	}
	
	_onResize() {
		if (!this.state.isHiding && Object.keys(this.props.order?.orderItems || {}).length) {
			if (!this.props.isNewOrder || this.state.isNotNewOrder) {
				if (this._onResizeST) {
					clearTimeout(this._onResizeST)
				}
				this._onResizeST = setTimeout(() => {
					const { mainWidth, minTableWidth } = this._getWidths()
					if (mainWidth !== this.state.mainWidth) {
						this.setState({ mainWidth })
						if (!this.state.formWidth) {
							this.setState({ formWidth: mainWidth })
						}
					}
				}, 10)
			}
		}
	}

	private _getWidths() {
		let mainWidth = 0
		let minTableWidth
		let querySelector = this.props.frameQuerySelector
		if (!querySelector) {
			querySelector = 'div[class^="orders_ordersTableFrame"] div.infinite-scroll-component'
		}
		const wrapperDiv = document?.body?.querySelector(querySelector)
		if (wrapperDiv) {
			const rect = wrapperDiv.getBoundingClientRect()
			mainWidth = rect.width
			if (querySelector.includes('ordersPage_tabs_body')) {
				try {
					const paddingLeft = window.getComputedStyle(wrapperDiv, null).getPropertyValue('padding-left') || 0
					const padding = +((paddingLeft + '').replace(/px/, '')) * 4
					mainWidth = (rect.width - padding)
				} catch(e) {}
			} else if (this._mainRef?.current?.querySelector('[class*="hasPackages"]')) {
				const div = this._mainRef.current.querySelector('[class*="hasPackages"]')
				const isInHierarchy = !!this._mainRef.current.closest('tr[class*="isInHierarchy"]')
				const halfEm = window.getComputedStyle(div, null).getPropertyValue('padding-left') || 0
				const widthExceptTable = +((halfEm + '').replace(/px/, '')) * (isInHierarchy ? 9.5 : 7.5)
				minTableWidth = (rect.width - widthExceptTable)
			}
		}
		return { mainWidth, minTableWidth }
	}
	
	private _cloneTheObject(object): any {
		let newObject = {}
		try {
			newObject = JSON.parse(JSON.stringify(object))
		} catch(e) {}
		return newObject
	}
	
	private async _doClone(isPartial?: boolean = false): void {
		if (typeof this.props.onNewOrder === 'function') {
			const { phone, isPhoneOk } = this._getPhone()
			let newOrder = this._cloneTheObject(this.props.order)
				newOrder.orderGuid = this.props.order.uuid
				newOrder.orderNumber = await this._getOrderNumber(isPartial)
				newOrder.authDealerId = this.props.authDealerId
				newOrder.dealerId = this.props.order.in_shop_id
				newOrder.isPartial = !!isPartial
				newOrder.onEdit = false
				newOrder.currency = this.props.order.currency
				newOrder.in_total = this.props.order.in_cost
				newOrder.in_point = ''
				newOrder.in_cust_phone = phone
				newOrder.isPhoneOk = isPhoneOk
				newOrder.client = {
					name: this.props.order.in_cust_name,
					email: this.props.order.in_cust_email,
					phone,
					manuallyEnteredCustomerAddress: this.props.order.manuallyEnteredCustomerAddress,
					pointAddress: this.props.order.pointAddress,
					pointDescription: this.props.order.pointDescription,
				}
				newOrder.fulfillmentStatus = null
				newOrder.outletExternalId = this.props.order.outletExternalId
				newOrder.outletDescription = this.props.order.outletDescription
				newOrder.isIntact = true
			let shipper
			const dealerSelect = new DealerSelect({
				dealerAndChildDealers: this.props.order.dealerAndChildDealers,
				order: this.props.order,
			})
			shipper = dealerSelect.getDefaultDealer()
			if (shipper?.id) {
				newOrder.shipperId = shipper.id
				newOrder.shipperName = shipper.name
			}
			if (!newOrder.mu_in_length) {
				newOrder.mu_in_length = newOrder.defaultPackageLengthMu
			}
			if (!newOrder.mu_in_width) {
				newOrder.mu_in_width = newOrder.defaultPackageWidthMu
			}
			if (!newOrder.mu_in_height) {
				newOrder.mu_in_height = newOrder.defaultPackageHeightMu
			}
			if (!newOrder.mu_in_weight) {
				newOrder.mu_in_weight = newOrder.defaultPackageWeightMu
			}
			if (!isPartial) {
				newOrder.origin_order_number = null
				newOrder.origin_order_dealer = null
			}
			if (newOrder.customer_address) {
				newOrder.customerDecodedAddress = newOrder.customer_address
			}
			this.props.onAction.send({
	            action: (!!isPartial ? 'Split' : 'Copy') + ' an order',
	            actionData: {
	              	file: 'components/OrdersPage/OrderItems.tsx',
	              	order: newOrder,
	            },
			})
			this.props.onNewOrder(newOrder)
		}
	}

	private _getOrderNumber(hasMaster): string {
		let masterOrderNumber = null
		if (hasMaster) {
			masterOrderNumber = (this.props.order.ext_key + '').substring(0, 30)
		}			
		let orderNumber = this.props.order.ext_key + '-1'
		return new Promise(success => {
			const object = {
				dealerId: this.props.order.in_shop_id,
				masterOrderNumber,
			}
			fetch('/api/v1/getOrderNumber', {
				method: 'POST',
				headers: {
					'Content-Type':'application/json'
				},
				body: JSON.stringify(object),
			}).then(response => {
				return response.json()
			}).then(json => {
				if (json.number) {
					orderNumber = (masterOrderNumber ? masterOrderNumber + '-' : '') + json.number
				}
				success(orderNumber)
			}).catch(error => {
				success(orderNumber)
			})
		}).catch(error => {
			return orderNumber
		})
	}
	
	private _onEditButton(): void {
		if (typeof this.props.onEditOrder === 'function') {
			const { phone, isPhoneOk } = this._getPhone()
			let editOrder = this._cloneTheObject(this.props.order)
				editOrder.orderGuid = this.props.order.uuid
				editOrder.orderNumber = this.props.order.ext_key
				editOrder.authDealerId = this.props.authDealerId
				editOrder.dealerId = this.props.order.in_shop_id
				editOrder.isPartial = false
				editOrder.onEdit = true
				editOrder.currency = this.props.order.currency
				editOrder.in_total = this.props.order.in_cost
				editOrder.in_point = this.props.order.in_point_uuid
				editOrder.in_cust_phone = phone
				editOrder.isPhoneOk = isPhoneOk
				editOrder.client = {
					name: this.props.order.in_cust_name,
					email: this.props.order.in_cust_email,
					phone: phone,
					manuallyEnteredCustomerAddress: this.props.order.manuallyEnteredCustomerAddress,
					pointAddress: this.props.order.pointAddress,
					pointDescription: this.props.order.pointDescription,
				}
				if (this.props.order.status === 'HOLD') {
					editOrder.fulfillmentStatus = 'HOLD'
				} else if (this.props.order.status === 'REJECTED') {
					editOrder.fulfillmentStatus = 'declined'
				} else if (this.props.order.status === 'CANCELLED') {
					editOrder.fulfillmentStatus = 'declined'
				}
				editOrder.outletExternalId = this.props.order.outletExternalId
				editOrder.outletDescription = this.props.order.outletDescription
				editOrder.isIntact = true
			let shipper
			const dealerSelect = new DealerSelect({
				dealerAndChildDealers: this.props.order.dealerAndChildDealers,
				order: this.props.order,
			})
			shipper = dealerSelect.getDefaultDealer()
			if (shipper?.id) {
				editOrder.shipperId = shipper.id
				editOrder.shipperName = shipper.name
			}
			if (!editOrder.mu_in_length) {
				editOrder.mu_in_length = editOrder.defaultPackageLengthMu
			}
			if (!editOrder.mu_in_width) {
				editOrder.mu_in_width = editOrder.defaultPackageWidthMu
			}
			if (!editOrder.mu_in_height) {
				editOrder.mu_in_height = editOrder.defaultPackageHeightMu
			}
			if (!editOrder.mu_in_weight) {
				editOrder.mu_in_weight = editOrder.defaultPackageWeightMu
			}
			if (editOrder.dealerAndChildDealers) {
				const defaultDealer = {}
				Object.keys(editOrder.dealerAndChildDealers).some(dealerId => {
					const dealer = editOrder.dealerAndChildDealers[dealerId]
					if (dealer.dealerType === 'isDefault') { // dealer from rds.orders
						defaultDealer[dealerId] = dealer
					}
				})
				editOrder.dealerAndChildDealers = defaultDealer
			}
			if (editOrder.customer_address) {
				editOrder.customerDecodedAddress = editOrder.customer_address
			}
			this.props.onAction.send({
	            action: 'Edit an order',
	            actionData: {
	              	file: 'components/OrdersPage/OrderItems.tsx',
	              	order: editOrder,
	            },
			})
			this.props.onEditOrder(editOrder)
			setTimeout(() => {
				editOrder.initial = this._cloneTheObject(editOrder)
			})
		}
	}

	private _getPhone(): void {
		let phoneString = (this.props.order?.in_cust_phone + '')
		const phoneLength = phoneString.replace(/\D+/g, '').length
		const country = (this.props.order.pointCountry || this.props.order.dealer_country)
		const isRu = (country === 'RU')
		const isUs = (country === 'US')
		const isGb = (country === 'UK' || country === 'GB')
		const isBelarus = phoneString.startsWith('375')
		const isKyrgyzstan = phoneString.startsWith('996')
		const isKazakhstan = phoneString.startsWith('997')
		let isPhoneLengthOk = false
		if (phoneLength === 11) {
			isPhoneLengthOk = true
		} else if (phoneLength === 10) {
			if (isRu) {
				phoneString = '7' + phoneString
				isPhoneLengthOk = true
			} else if (isUs) {
				phoneString = '1' + phoneString
				isPhoneLengthOk = true
			} else if (isGb) {
				phoneString = '44' + phoneString
				isPhoneLengthOk = true
			}
		} else if (phoneLength === 12) {
			if (isBelarus || isKyrgyzstan || isKazakhstan) {
				isPhoneLengthOk = true
			}
		}
		return { phone: phoneString, isPhoneOk: isPhoneLengthOk }
	}
	
	private _stopEditing(event): void {
		event.stopPropagation()
		this.setState({ isEditing: false })
		if (typeof this.props.onEdited === 'function') {
			this.props.onEdited(this._cloneTheObject(this.state.orderItems))
		}
		this._removeExtraWidth()
		if (this.props.setItemsTableVisible) {
			this.props.setItemsTableVisible(false)
		}
	}
	
	private _toHideTheOrder(): void {
		if (this.props.isNewOrder) {
			if (this.props.order.isNew) {
				this.props.setIsNewOrder(false)
				this.props.onNewOrder({})
			}
		}
		this.setState({
			isHiding: true,
		})
		if (typeof this.props.setOrdersWithShownItems === 'function') {
			const newList = JSON.parse(JSON.stringify(this.props.ordersWithShownItems))
			if (newList[this.props.order.id]) {
				delete newList[this.props.order.id]
			}
			this.props.setOrdersWithShownItems(newList)
		}
		if (this.props.setItemsTableVisible) {
			this.props.setItemsTableVisible(false)
			this._removeExtraWidth()
		}
	}
	
	private _removeExtraWidth(): void {
		const modal = document?.body.querySelector('#react-aria-modal-dialog')
		if (modal) {
			modal.style.width = null
		}
	}
	
	private _onSaveButton(): void {
		this._toHideTheOrder()
	}
	private _onCancelButton(): void {
		this._toHideTheOrder()
	}
	
	private _onExternalEvent(type, value): void {
		if (type === 'ordersRowClick') {
			if (this.state.isHiding) {
				this.setState({ isHiding: false })
			}
		}
	}
}